import {
  AddRounded,
  ArrowBackRounded,
  CheckCircleRounded,
  CheckRounded,
  ChevronRight,
  CloseRounded,
  DescriptionOutlined,
  EditOutlined,
  InfoOutlined,
  LockOpenOutlined,
  LockOutlined,
  RadioButtonUnchecked,
  ScheduleOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  daysUntilDateOnly,
  formatDate,
} from "../../../../_src_shared/utils/functions/formatDate";
import Flx from "../../../../components/layout/Flx";
import { usePermissionsHook } from "../../../../hooks/usePermissionsHook";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import {
  sidebarSetKeyValue,
  sidebarTypeSet,
} from "../../../../redux/actions/sidebarActions";
import { filePreviewSetData } from "../../../../redux/reducers/filePreviewReducer";
import { formatBoolean } from "../../../../utils/booleans/formatBoolean";
import { formatDollar } from "../../../../utils/numbers/formatDollar";
import { formatNumber } from "../../../../utils/numbers/formatNumber";
import { formatPercent } from "../../../../utils/numbers/formatPercent";
import LoanDrilldownTabPanel from "../LoanDrilldownTabPanel";
import {
  getLockPricingMode,
  getLockStage,
  isLockCurrentlyExpired,
  normalizeLockToCamel,
} from "./loanLockUtils";
const VALUE_FONT_SIZE = 13;
/* Design tokens — slate neutrals + indigo accent + semantic
   amber (pending) / emerald (confirmed) / rose (expired). */
const T = {
  ink: "#0f172a",
  ink2: "#334155",
  muted: "#64748b",
  faint: "#94a3b8",
  line: "#e2e8f0",
  lineSoft: "#eef2f6",
  surface: "#ffffff",
  canvas: "#f6f8fb",
  accent: "#4f46e5",
  accentSoft: "#eef2ff",
  amber: "#b45309",
  amberSoft: "#fffbeb",
  amberLine: "#fde68a",
  emerald: "#047857",
  emeraldSoft: "#ecfdf5",
  emeraldLine: "#a7f3d0",
  rose: "#be123c",
  roseSoft: "#fff1f2",
  roseLine: "#fecdd3",
};
const FONT_NUM = "ui-monospace, 'SF Mono', Menlo, monospace";

const CARD_SX = {
  background: T.surface,
  border: `1px solid ${T.line}`,
  borderRadius: "14px",
  p: "22px",
  boxShadow: "0 1px 2px rgba(15,23,42,.04)",
};

/* ═══════════════════════════════════════════
   PRIMITIVES
   ═══════════════════════════════════════════ */

const TONE_MAP = {
  pending: { bg: T.amberSoft, fg: T.amber, bd: T.amberLine },
  confirmed: { bg: T.emeraldSoft, fg: T.emerald, bd: T.emeraldLine },
  expired: { bg: T.roseSoft, fg: T.rose, bd: T.roseLine },
  neutral: { bg: "#f1f5f9", fg: T.ink2, bd: T.line },
};

const Pill = ({ tone = "neutral", icon, children }) => {
  const c = TONE_MAP[tone] || TONE_MAP.neutral;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.bd}`,
        borderRadius: "999px",
        px: 1.25,
        py: 0.5,
        fontSize: 12.5,
        fontWeight: 600,
        whiteSpace: "nowrap",
        "& svg": { fontSize: 14 },
      }}
    >
      {icon}
      {children}
    </Box>
  );
};

const Eyebrow = ({ children, sx }) => (
  <Box
    sx={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.8,
      color: T.faint,
      textTransform: "uppercase",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const CardTitle = ({ children }) => (
  <Box sx={{ fontSize: 15, fontWeight: 700, color: T.ink }}>{children}</Box>
);

const Datum = ({ label, value, mono, tone }) => {
  const isEmptyVal = isNil(value) || value === "" || value === "—";
  return (
    <Box>
      <Box sx={{ fontSize: 11.5, color: T.faint, fontWeight: 600, mb: 0.5 }}>
        {label}
      </Box>
      <Box
        sx={{
          fontSize: VALUE_FONT_SIZE,
          fontWeight: 600,
          color: tone || (isEmptyVal ? T.faint : T.ink),
          fontFamily: mono && !isEmptyVal ? FONT_NUM : "inherit",
        }}
      >
        {isEmptyVal ? "—" : value}
      </Box>
    </Box>
  );
};

/* lifecycle stepper: 0 none · 1 requested · 2 confirmed · 3 expired */
const Lifecycle = ({ stage }) => {
  const steps = [
    { key: "request", label: "Requested" },
    { key: "confirm", label: "Confirmed" },
    { key: "active", label: stage === 3 ? "Expired" : "Active" },
  ];
  const reached = (i) => (stage === 3 ? true : i < stage);
  return (
    <Flx ac sx={{ width: "100%" }}>
      {steps.map((s, i) => {
        const done = reached(i);
        const isExpiredEnd = stage === 3 && i === 2;
        const dotColor = isExpiredEnd ? T.rose : done ? T.emerald : "#cbd5e1";
        return (
          <Box key={s.key} sx={{ display: "contents" }} component="div">
            <Flx column ac g={0.75}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  background: done
                    ? isExpiredEnd
                      ? T.roseSoft
                      : T.emeraldSoft
                    : "#f1f5f9",
                  border: `1.5px solid ${
                    done ? (isExpiredEnd ? T.roseLine : T.emeraldLine) : T.line
                  }`,
                  color: dotColor,
                  "& svg": { fontSize: 14 },
                }}
              >
                {isExpiredEnd ? (
                  <CloseRounded />
                ) : done ? (
                  <CheckRounded />
                ) : (
                  <RadioButtonUnchecked />
                )}
              </Box>
              <Box
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: done ? (isExpiredEnd ? T.rose : T.ink2) : T.faint,
                }}
              >
                {s.label}
              </Box>
            </Flx>
            {i < steps.length - 1 && (
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  mx: 1,
                  mb: "18px",
                  borderRadius: 2,
                  minWidth: 28,
                  background: reached(i + 1) ? T.emeraldLine : T.line,
                }}
              />
            )}
          </Box>
        );
      })}
    </Flx>
  );
};

const RailCard = ({ title, children }) => (
  <Box sx={{ ...CARD_SX, borderRadius: "13px", p: "18px" }}>
    <Eyebrow sx={{ mb: 1.5 }}>{title}</Eyebrow>
    {children}
  </Box>
);

/* ═══════════════════════════════════════════
   MAIN
   ═══════════════════════════════════════════ */

const LoanDrilldownLoanLockTab = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canViewLoanLock, isCapitalMarkets } = usePermissionsHook();
  const loan = useSelector((state) => state.loanDrilldown);

  // The backend mirrors the FULL snake_case lock doc onto deal.loanLock; adapt it
  // to the camelCase shape this view reads. Stage is derived off lock_status.
  const rawLock = loan?.loanLock;
  const loanLock = useMemo(() => normalizeLockToCamel(rawLock), [rawLock]);
  const isExpired = useMemo(() => isLockCurrentlyExpired(loanLock), [loanLock]);

  // 0 none · 1 requested · 2 confirmed · 3 expired
  const stage = useMemo(() => getLockStage(rawLock), [rawLock]);

  // UW, admin, capital markets, sales / loan officers, processing, closers.
  const canViewLock = canViewLoanLock;
  // Open the lock sidebar in its default (confirm/return/manage) view.
  const openLockSidebar = () => {
    dispatch(sidebarSetKeyValue("params", null));
    dispatch(sidebarTypeSet(loanDrilldownSidebarTypes.ADD_EDIT_LOAN_LOCK));
  };
  // Open the lock sidebar straight into the price-terms edit form.
  const openEditLock = () => {
    dispatch(sidebarSetKeyValue("params", { mode: "edit" }));
    dispatch(sidebarTypeSet(loanDrilldownSidebarTypes.ADD_EDIT_LOAN_LOCK));
  };

  if (!canViewLock) {
    return (
      <LoanDrilldownTabPanel>
        <Box sx={{ background: T.canvas, minHeight: "100%", p: 3.5 }}>
          <Box sx={{ ...CARD_SX, maxWidth: 1180, mx: "auto" }}>
            <Flx column ac g={1} sx={{ py: 6 }}>
              <LockOutlined sx={{ fontSize: 36, color: T.faint }} />
              <Box sx={{ color: T.muted, fontSize: 13.5 }}>
                Visible to underwriting and admin roles only.
              </Box>
            </Flx>
          </Box>
        </Box>
      </LoanDrilldownTabPanel>
    );
  }

  return (
    <LoanDrilldownTabPanel>
      <Box
        sx={{
          background: T.canvas,
          minHeight: "100%",
          color: T.ink,
          p: { xs: 2, md: 3.5 },
        }}
      >
        <Box sx={{ maxWidth: 1180, mx: "auto" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 320px" },
              gap: 2.75,
              alignItems: "start",
            }}
          >
            {/* main column */}
            <Flx column g={2.25}>
              {stage === 0 ? (
                <EmptyState loan={loan} onSetLock={openLockSidebar} />
              ) : (
                <>
                  <StatusHeaderCard
                    loanLock={loanLock}
                    stage={stage}
                    onEdit={openLockSidebar}
                    canManage={isCapitalMarkets}
                  />
                  {loanLock?.actionNeeded?.reason && (
                    <ActionNeededCallout actionNeeded={loanLock.actionNeeded} />
                  )}
                  <LockDetailsCard
                    loanLock={loanLock}
                    loan={loan}
                    stage={stage}
                  />
                  <PriceTermsCard
                    loanLock={loanLock}
                    loan={loan}
                    canManage={isCapitalMarkets}
                    onEdit={openEditLock}
                  />
                  {stage === 3 && (
                    <ExpiredCallout
                      onRequestExtension={openLockSidebar}
                      canManage={isCapitalMarkets}
                    />
                  )}
                  <TermsAtLockCard
                    loanLock={loanLock}
                    loan={loan}
                    stage={stage}
                  />
                </>
              )}
            </Flx>

            {/* right rail */}
            <Flx column g={2}>
              <PricingPathCard loan={loan} />
              {stage > 0 && (
                <ApprovalTrailCard loanLock={loanLock} stage={stage} />
              )}
              {stage > 0 && (
                <RailCard title="Lock countdown">
                  <LockCountdownBody
                    loanLock={loanLock}
                    isExpired={isExpired}
                  />
                </RailCard>
              )}
              <VisibilityNote />
            </Flx>
          </Box>

          {/* Legacy view — return to the current (redesigned) Loan Lock tab. */}
          <Flx
            center
            sx={{ mt: 4, pt: 3, borderTop: `1px solid ${T.line}` }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate("../loan-lock")}
              startIcon={<ArrowBackRounded sx={{ fontSize: 16 }} />}
              sx={{ textTransform: "none", borderRadius: "10px" }}
            >
              Back to current view
            </Button>
          </Flx>
        </Box>
      </Box>
    </LoanDrilldownTabPanel>
  );
};

/* ── returned-to-requester callout ── */
const ActionNeededCallout = ({ actionNeeded }) => (
  <Flx
    g={1.5}
    sx={{
      alignItems: "flex-start",
      background: T.roseSoft,
      border: `1px solid ${T.roseLine}`,
      borderRadius: "12px",
      p: 2,
    }}
  >
    <WarningAmberOutlined
      sx={{ fontSize: 20, color: T.rose, flexShrink: 0, mt: "1px" }}
    />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ fontSize: 13.5, fontWeight: 700, color: T.rose, mb: 0.4 }}>
        Returned to requester — action needed
      </Box>
      <Box sx={{ fontSize: 13, color: T.ink2, lineHeight: 1.5 }}>
        {actionNeeded.reason}
      </Box>
      <Box sx={{ fontSize: 11.5, color: T.muted, mt: 0.85 }}>
        {actionNeeded.requiresException
          ? "A pricing-exception request is required before this lock can be confirmed."
          : "The requester must provide an update before this lock can be confirmed."}
        {actionNeeded.flaggedBy ? ` · Flagged by ${actionNeeded.flaggedBy}` : ""}
        {actionNeeded.flaggedDateTime
          ? ` · ${formatDate(actionNeeded.flaggedDateTime)}`
          : ""}
      </Box>
    </Box>
  </Flx>
);

/* ── status header + lifecycle ── */
const StatusHeaderCard = ({ loanLock, stage, onEdit, canManage }) => {
  // The trade counterparty is capital-markets-only and is never surfaced in the
  // drilldown (see rate-locks.md trade-counterparty barrier) — only the queue.
  const actionNeeded = loanLock?.actionNeeded?.reason
    ? loanLock.actionNeeded
    : null;
  const meta = useMemo(() => {
    if (stage === 1 && actionNeeded)
      return {
        tone: "expired",
        icon: <WarningAmberOutlined />,
        text: "Returned to requester",
        pill: (
          <Pill tone="expired" icon={<WarningAmberOutlined />}>
            Action needed
          </Pill>
        ),
      };
    if (stage === 1)
      return {
        tone: "pending",
        icon: <ScheduleOutlined />,
        text: "Lock requested",
        pill: (
          <Pill tone="pending" icon={<ScheduleOutlined />}>
            Awaiting confirmation
          </Pill>
        ),
      };
    if (stage === 3)
      return {
        tone: "expired",
        icon: <WarningAmberOutlined />,
        text: "Lock expired — re-lock required",
        pill: loanLock?.lockExpirationDate ? (
          <Pill tone="expired" icon={<WarningAmberOutlined />}>
            Expired {formatDate(loanLock?.lockExpirationDate)}
          </Pill>
        ) : (
          <Pill tone="expired" icon={<WarningAmberOutlined />}>
            Expired
          </Pill>
        ),
      };
    return {
      tone: "confirmed",
      icon: <LockOutlined />,
      text: "Locked",
      pill: (
        <Pill tone="confirmed" icon={<CheckCircleRounded />}>
          Confirmed
        </Pill>
      ),
    };
  }, [stage, loanLock?.lockExpirationDate, actionNeeded]);

  const iconTones = {
    pending: { bg: T.amberSoft, fg: T.amber },
    confirmed: { bg: T.emeraldSoft, fg: T.emerald },
    expired: { bg: T.roseSoft, fg: T.rose },
  };
  const it = iconTones[meta.tone];

  return (
    <Box sx={CARD_SX}>
      <Flx jb sx={{ alignItems: "flex-start", gap: 2, mb: 2.5 }}>
        <Flx g={1.75}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              background: it.bg,
              color: it.fg,
              "& svg": { fontSize: 21 },
            }}
          >
            {meta.icon}
          </Box>
          <Box>
            <Eyebrow sx={{ mb: 0.4 }}>Lock Status</Eyebrow>
            <Box sx={{ fontSize: 18, fontWeight: 700, color: T.ink }}>
              {meta.text}
            </Box>
            <Box sx={{ mt: 1.1 }}>{meta.pill}</Box>
          </Box>
        </Flx>
        {canManage ? (
          <Button
            onClick={onEdit}
            startIcon={<EditOutlined sx={{ fontSize: 14 }} />}
            sx={{
              flexShrink: 0,
              border: `1px solid ${T.line}`,
              background: T.surface,
              color: T.ink2,
              borderRadius: "9px",
              px: 1.75,
              py: 1,
              fontSize: 13,
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { background: "#f8fafc" },
            }}
          >
            {stage === 1 ? "Confirm lock" : "Manage lock"}
          </Button>
        ) : (
          <Flx
            ac
            g={0.5}
            sx={{
              flexShrink: 0,
              border: `1px solid ${T.lineSoft}`,
              borderRadius: "9px",
              px: 1.5,
              py: 1,
              fontSize: 12,
              fontWeight: 600,
              color: T.faint,
              "& svg": { fontSize: 14 },
            }}
          >
            <LockOutlined /> Capital markets only
          </Flx>
        )}
      </Flx>

      <Box sx={{ pt: 2.25, px: 0.75, borderTop: `1px solid ${T.lineSoft}` }}>
        <Lifecycle stage={stage} />
      </Box>
    </Box>
  );
};

/* ── lock details ── */
const LockDetailsCard = ({ loanLock, loan, stage }) => {
  const dispatch = useDispatch();
  const mode = getLockPricingMode(loan?.loanProductType);
  const pricingNote =
    mode === "price"
      ? "Bond-priced · price"
      : mode === "rate"
        ? "Short-term pricing · buy rate + spread"
        : "Pricing · rate or price";
  const isExpired = stage === 3;
  const notes =
    loanLock?.lockNotes ||
    (stage === 1
      ? "Lock requested pending pricing confirmation."
      : "No notes provided.");

  return (
    <Box sx={CARD_SX}>
      <Flx jb ac sx={{ mb: 2.25 }}>
        <CardTitle>Lock details</CardTitle>
        <Box sx={{ fontSize: 11.5, color: T.faint, fontWeight: 600 }}>
          {pricingNote}
        </Box>
      </Flx>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr) minmax(0, 1fr)",
            sm: "repeat(3, minmax(0, 1fr))",
          },
          rowGap: 2.5,
          columnGap: 2,
        }}
      >
        <Datum
          label="Requested"
          value={formatDate(
            loanLock?.lockRequestedDate || loanLock?.createDateTime,
          )}
        />
        <Datum
          label="Confirmed"
          value={formatDate(loanLock?.lockConfirmationDate)}
        />

        <Datum
          label="Expires"
          value={formatDate(loanLock?.lockExpirationDate)}
          tone={isExpired ? T.rose : undefined}
        />
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{ fontSize: 11.5, color: T.faint, fontWeight: 600, mb: 0.5 }}
          >
            Loan sizer
          </Box>
          {loanLock?.loanSizer?.file_display_name ? (
            <Box
              role="button"
              title={loanLock.loanSizer.file_display_name}
              onClick={() => dispatch(filePreviewSetData(loanLock.loanSizer))}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                width: "100%",
                minWidth: 0,
                color: T.accent,
                fontSize: VALUE_FONT_SIZE,
                fontWeight: 600,
                cursor: "pointer",
                "&:hover .ll-sizer-name": { textDecoration: "underline" },
              }}
            >
              <DescriptionOutlined sx={{ fontSize: 15, flexShrink: 0 }} />
              <Box
                className="ll-sizer-name"
                sx={{
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {loanLock.loanSizer.file_display_name}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{ fontSize: VALUE_FONT_SIZE, fontWeight: 600, color: T.faint }}
            >
              —
            </Box>
          )}
        </Box>
        <Datum
          label="Pre-close review"
          value={formatBoolean(loanLock?.preCloseReviewComplete, {
            yes: "Completed",
            no: "Not completed",
            fallback: "Not completed",
          })}
          tone={loanLock?.preCloseReviewComplete ? T.emerald : T.ink2}
        />
      </Box>

      <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${T.lineSoft}` }}>
        <Box sx={{ fontSize: 11.5, color: T.faint, fontWeight: 600, mb: 0.6 }}>
          Notes
        </Box>
        <Box
          sx={{
            fontSize: 13.5,
            color: T.ink2,
            lineHeight: 1.55,
            whiteSpace: "pre-line",
          }}
        >
          {notes}
        </Box>
      </Box>
    </Box>
  );
};

/* ── price terms (the requester's submitted pricing) ── */
const PriceTermsCard = ({ loanLock, loan, canManage, onEdit }) => {
  const product =
    loanLock?.product ||
    (getLockPricingMode(loan?.loanProductType) === "price" ? "dscr" : "rtl");
  const isRtl = product === "rtl";
  const pct = (v) => (isNil(v) ? null : formatPercent(v));
  const num = (v) => (isNil(v) ? null : formatNumber(v));

  const items = isRtl
    ? [
        ["Coupon rate", pct(loanLock?.coupon_rate)],
        ["Buy rate", pct(loanLock?.lock_buy_rate)],
        ["Rate spread", pct(loanLock?.lock_rate_spread)],
        ["Pricing base rate", pct(loanLock?.pricing_base_rate)],
        ["Pricing spread", pct(loanLock?.pricing_spread)],
        ["Risk spread adj.", pct(loanLock?.risk_spread_adjustment)],
        ["Override buy rate", pct(loanLock?.override_buy_rate)],
        [
          "Borrower classification",
          loanLock?.borrower_classification_override ||
            loanLock?.borrower_classification_calculated,
        ],
      ]
    : [
        ["Rate at lock", pct(loanLock?.rate_at_lock)],
        ["Base price (pts)", num(loanLock?.base_price_for_rate)],
        ["Total exit price", num(loanLock?.total_exit_price)],
        ["Margin tier", loanLock?.margin_tier],
        ["Margin (pts)", num(loanLock?.actual_margin ?? loanLock?.margin_points_at_lock)],
        [
          "Calculated DSCR",
          isNil(loanLock?.calculated_dscr_at_lock)
            ? null
            : `${loanLock.calculated_dscr_at_lock}x`,
        ],
      ];

  return (
    <Box sx={CARD_SX}>
      <Flx jb ac sx={{ mb: 2.25 }}>
        <CardTitle>Price terms</CardTitle>
        {canManage && onEdit && (
          <Button
            size="small"
            variant="text"
            startIcon={<EditOutlined sx={{ fontSize: 14 }} />}
            onClick={onEdit}
            sx={{ textTransform: "none", fontSize: 12.5, fontWeight: 600 }}
          >
            Edit
          </Button>
        )}
      </Flx>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" },
          rowGap: 2.5,
          columnGap: 2,
        }}
      >
        {items.map(([label, value]) => (
          <Datum key={label} label={label} value={value} mono />
        ))}
      </Box>
    </Box>
  );
};

/* ── expired callout ── */
const ExpiredCallout = ({ onRequestExtension, canManage }) => (
  <Flx
    ac
    g={1.5}
    sx={{
      background: T.roseSoft,
      border: `1px solid ${T.roseLine}`,
      borderRadius: "12px",
      p: 2,
    }}
  >
    <WarningAmberOutlined sx={{ fontSize: 18, color: T.rose }} />
    <Box sx={{ fontSize: 13, color: T.rose, flex: 1, lineHeight: 1.45 }}>
      <strong>This lock has expired.</strong>{" "}
      {canManage
        ? "Extend the lock or request a new one to proceed to closing."
        : "The capital-markets desk must extend the lock or request a new one."}
    </Box>
    {canManage && (
      <Button
        onClick={onRequestExtension}
        sx={{
          border: `1px solid ${T.rose}`,
          background: T.surface,
          color: T.rose,
          borderRadius: "8px",
          px: 1.6,
          py: 0.9,
          fontSize: 12.5,
          fontWeight: 600,
          textTransform: "none",
          whiteSpace: "nowrap",
          "&:hover": { background: T.roseSoft },
        }}
      >
        Request extension
      </Button>
    )}
  </Flx>
);

/* ── terms-at-lock snapshot ── */
const CHANGE_FIELDS = [
  "baseLoanAmount",
  "totalLoanAmount",
  "interestRate",
  "termMonths",
  "maturityDate",
];

// Humanize a snapshot key + best-effort format its value for the "View all" grid.
const humanizeKey = (k) =>
  String(k)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());

const formatSnapshotValue = (key, v) => {
  if (isNil(v) || v === "") return null;
  if (typeof v === "boolean")
    return formatBoolean(v, { yes: "Yes", no: "No" });
  const lk = String(key).toLowerCase();
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v))
    return formatDate(v);
  if (lk.includes("date")) return typeof v === "string" ? formatDate(v) : v;
  if (typeof v === "number") {
    if (lk.includes("rate")) return formatPercent(v);
    if (lk.includes("points")) return formatPercent(v);
    if (lk === "dscr") return `${v}x`;
    if (lk.includes("month")) return `${formatNumber(v)} mo`;
    if (
      lk.includes("amount") ||
      lk.includes("loan") ||
      lk.includes("holdback") ||
      lk.includes("reserve") ||
      lk.includes("upb") ||
      lk.includes("price")
    )
      return formatDollar(v);
    return formatNumber(v);
  }
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const TermsAtLockCard = ({ loanLock, loan, stage }) => {
  const [showAll, setShowAll] = useState(false);
  const snapshot = loanLock?.loanTermsAtLock;
  const hasSnapshot = !isNil(snapshot) && !isEmpty(snapshot);

  const allEntries = useMemo(
    () =>
      hasSnapshot
        ? Object.entries(snapshot).sort(([a], [b]) => a.localeCompare(b))
        : [],
    [hasSnapshot, snapshot],
  );

  const changeCount = useMemo(() => {
    if (!hasSnapshot) return 0;
    return CHANGE_FIELDS.reduce((n, key) => {
      const a = snapshot?.[key];
      const b = loan?.[key];
      if (isNil(a) || isNil(b)) return n;
      return String(a) !== String(b) ? n + 1 : n;
    }, 0);
  }, [hasSnapshot, snapshot, loan]);

  return (
    <Box sx={CARD_SX}>
      <Flx jb ac>
        <CardTitle>Terms at lock</CardTitle>
        <Flx ac g={1}>
          {changeCount > 0 && (
            <Pill tone="pending" icon={<WarningAmberOutlined />}>
              {changeCount} changed since lock
            </Pill>
          )}
          {hasSnapshot && (
            <Box
              component="button"
              type="button"
              onClick={() => setShowAll((v) => !v)}
              sx={{
                font: "inherit",
                fontSize: 12.5,
                fontWeight: 600,
                color: T.accent,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {showAll ? "Show less" : "View all"}
            </Box>
          )}
        </Flx>
      </Flx>

      {!hasSnapshot ? (
        <Box sx={{ mt: 1, fontSize: 13, color: T.faint }}>
          {stage === 1
            ? "Snapshot is captured the moment the lock is confirmed."
            : "No snapshot was captured at lock time."}
        </Box>
      ) : showAll ? (
        <Box
          sx={{
            mt: 1.75,
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" },
            gap: 2,
          }}
        >
          {allEntries.map(([k, v]) => (
            <Datum
              key={k}
              label={humanizeKey(k)}
              value={formatSnapshotValue(k, v)}
              mono={typeof v === "number"}
            />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            mt: 1.75,
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
            gap: 2,
          }}
        >
          <Datum
            label="Base amount"
            value={
              isNil(snapshot?.baseLoanAmount)
                ? null
                : formatDollar(snapshot?.baseLoanAmount)
            }
            mono
          />
          <Datum
            label="Total loan"
            value={
              isNil(snapshot?.totalLoanAmount)
                ? null
                : formatDollar(snapshot?.totalLoanAmount)
            }
            mono
          />
          <Datum
            label="Interest rate"
            value={
              isNil(snapshot?.interestRate)
                ? null
                : formatPercent(snapshot?.interestRate)
            }
            mono
          />
          <Datum
            label="Term"
            value={
              isNil(snapshot?.termMonths)
                ? null
                : `${formatNumber(snapshot?.termMonths)} mo`
            }
            mono
          />
          <Datum label="Status at lock" value={loanLock?.loanStatusAtLock} />
          <Datum label="Product" value={snapshot?.loanProductType} />
          <Datum label="Purpose" value={snapshot?.loanPurpose} />
          <Datum label="Entity" value={snapshot?.entity_name} />
        </Box>
      )}
    </Box>
  );
};

/* ── right rail: pricing path ── */
const PricingPathCard = ({ loan }) => {
  const mode = getLockPricingMode(loan?.loanProductType);
  const product = loan?.loanProductType || "This product";
  const heading =
    mode === "price" ? "Bond-priced" : mode === "rate" ? "Short-term" : "Mixed";
  const copy =
    mode === "price"
      ? `${product} loans lock on price (bond points).`
      : mode === "rate"
        ? `${product} loans lock with a buy rate and rate spread — no price.`
        : `${product} loans can lock on rate or price.`;
  return (
    <RailCard title="Pricing path">
      <Box sx={{ fontSize: 15, fontWeight: 700, color: T.ink, mb: 0.5 }}>
        {heading}
      </Box>
      <Box sx={{ fontSize: 12.5, color: T.muted, lineHeight: 1.5 }}>{copy}</Box>
    </RailCard>
  );
};

/* ── right rail: approval trail ── */
const initialsOf = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "—";

const TrailRow = ({ name, meta, org, color, bg, muted }) => (
  <Flx g={1.4} sx={{ alignItems: "flex-start" }}>
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        background: bg,
        color,
        display: "grid",
        placeItems: "center",
        fontSize: 12,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {name && !muted ? initialsOf(name) : "—"}
    </Box>
    <Box
      sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}
    >
      <Box
        sx={{
          fontSize: 13.5,
          fontWeight: 600,
          lineHeight: 1.3,
          color: muted ? T.faint : T.ink,
        }}
      >
        {name}
      </Box>
      <Box sx={{ fontSize: 11.5, lineHeight: 1.4, color: T.muted }}>{meta}</Box>
      {org && (
        <Box sx={{ fontSize: 11.5, lineHeight: 1.4, color: T.faint }}>
          {org}
        </Box>
      )}
    </Box>
  </Flx>
);

const ApprovalTrailCard = ({ loanLock, stage }) => {
  const requester = loanLock?.lockRequestUser;
  const confirmer = loanLock?.lockConfirmUser;
  const requestedMeta = `Requested${
    loanLock?.lockRequestedDate
      ? ` · ${formatDate(loanLock?.lockRequestedDate)}`
      : ""
  }`;
  const confirmedMeta = `Confirmed${
    loanLock?.lockConfirmationDate || confirmer?.completionDateTime
      ? ` · ${formatDate(loanLock?.lockConfirmationDate || confirmer?.completionDateTime)}`
      : ""
  }`;
  return (
    <RailCard title="Approval trail">
      <TrailRow
        name={requester?.fullName || "Not recorded"}
        meta={requestedMeta}
        org={requester?.organization}
        color={T.accent}
        bg={T.accentSoft}
        muted={!requester?.fullName}
      />
      <Box sx={{ height: 14 }} />
      {stage >= 2 && confirmer?.fullName ? (
        <TrailRow
          name={confirmer.fullName}
          meta={confirmedMeta}
          org={confirmer?.roles}
          color={T.emerald}
          bg={T.emeraldSoft}
        />
      ) : (
        <TrailRow
          name="Not recorded"
          meta="Confirmed"
          color={T.faint}
          bg="#f1f5f9"
          muted
        />
      )}
      {!isNil(loanLock?.updateDateTime) && (
        <Box
          sx={{
            mt: 1.75,
            pt: 1.25,
            borderTop: `1px solid ${T.lineSoft}`,
            fontSize: 11,
            color: T.faint,
          }}
        >
          Last updated {formatDate(loanLock?.updateDateTime)}
        </Box>
      )}
    </RailCard>
  );
};

/* ── right rail: countdown ── */
const LockCountdownBody = ({ loanLock, isExpired }) => {
  const endDate = loanLock?.lockExpirationDate;
  const startDate =
    loanLock?.lockConfirmationDate ||
    loanLock?.lockRequestedDate ||
    loanLock?.createDateTime;

  const daysRemaining = useMemo(() => {
    if (isNil(endDate)) return null;
    return daysUntilDateOnly(endDate);
  }, [endDate]);

  const pct = useMemo(() => {
    if (isNil(startDate) || isNil(endDate)) return 0;
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    if (isNaN(start) || isNaN(end) || end <= start) return isExpired ? 0 : 1;
    const remaining = (end - Date.now()) / (end - start);
    return Math.min(1, Math.max(0, remaining));
  }, [startDate, endDate, isExpired]);

  if (isNil(endDate)) {
    return (
      <Box sx={{ fontSize: 13, color: T.faint }}>No expiration date set.</Box>
    );
  }

  if (isExpired) {
    return (
      <Box sx={{ fontSize: 13, color: T.rose, fontWeight: 600 }}>
        Expired{" "}
        {daysRemaining != null ? `${Math.abs(daysRemaining)} days ago` : ""}
      </Box>
    );
  }

  const days = Math.max(0, daysRemaining ?? 0);
  return (
    <Box>
      <Flx sx={{ alignItems: "baseline", gap: 0.75, mb: 1.25 }}>
        <Box
          sx={{
            fontSize: 28,
            fontWeight: 700,
            color: T.ink,
            fontFamily: FONT_NUM,
          }}
        >
          {days}
        </Box>
        <Box sx={{ fontSize: 13, color: T.muted, fontWeight: 600 }}>
          days remaining
        </Box>
      </Flx>
      <Box
        sx={{
          height: 7,
          borderRadius: "99px",
          background: "#eef2f6",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${pct * 100}%`,
            height: "100%",
            borderRadius: "99px",
            background: pct > 0.3 ? T.emerald : T.amber,
          }}
        />
      </Box>
      <Box sx={{ fontSize: 11.5, color: T.faint, mt: 1 }}>
        Expires {formatDate(endDate)}
      </Box>
    </Box>
  );
};

/* ── right rail: visibility note ── */
const VisibilityNote = () => (
  <Flx
    g={1.25}
    sx={{
      alignItems: "flex-start",
      background: T.amberSoft,
      border: `1px solid ${T.amberLine}`,
      borderRadius: "11px",
      p: "12px 14px",
    }}
  >
    <InfoOutlined
      sx={{ fontSize: 15, color: T.amber, mt: "1px", flexShrink: 0 }}
    />
    <Box sx={{ fontSize: 12, color: T.amber, lineHeight: 1.45 }}>
      Visible to underwriting and admin roles only.
    </Box>
  </Flx>
);

/* ═══════════════════════════════════════════
   EMPTY STATE — hero + readiness checklist
   ═══════════════════════════════════════════ */

const EmptyState = ({ loan, onSetLock }) => {
  const navigate = useNavigate();

  const checklist = useMemo(() => {
    const sizer = loan?.loanSizer;
    const sizerAttached = !isEmpty(sizer);
    const rate = loan?.interestRate;
    const amount = loan?.totalLoanAmount;
    const termsConfirmed = !isNil(rate) && !isNil(amount);
    const status = loan?.loanStatus;
    const uwApproved = /clear to close|approved/i.test(String(status || ""));
    return [
      {
        label: "Loan sizer attached",
        done: sizerAttached,
        // shown when complete
        doneText: sizer?.file_display_name || "On file",
        // shown when incomplete: what + how
        todoText:
          "Helps the desk price the lock. Upload one on Documents — or attach it right in the request form.",
        action: { label: "Open Documents", to: "../documents" },
      },
      {
        label: "Loan terms confirmed",
        done: termsConfirmed,
        doneText:
          [
            !isNil(rate) ? formatPercent(rate) : null,
            !isNil(amount) ? formatDollar(amount) : null,
          ]
            .filter(Boolean)
            .join(" · ") || "On file",
        todoText:
          "Set the interest rate, term, and loan amount so they're captured at lock.",
        action: { label: "Open Loan Terms", to: "../terms" },
      },
      {
        label: "Underwriting approved",
        done: uwApproved,
        doneText: status ? `Status: ${status}` : "Approved",
        todoText: status
          ? `Currently ${status} — underwriting approval is recommended before locking.`
          : "Underwriting status pending.",
        action: { label: "Open Checklist", to: "../checklist" },
      },
    ];
  }, [loan]);

  const doneCount = checklist.filter((c) => c.done).length;
  const ready = doneCount === checklist.length;

  return (
    <>
      <Box sx={{ ...CARD_SX, p: "44px 24px", textAlign: "center" }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "16px",
            background: T.accentSoft,
            color: T.accent,
            display: "grid",
            placeItems: "center",
            mx: "auto",
            mb: 2,
            "& svg": { fontSize: 26 },
          }}
        >
          <LockOpenOutlined />
        </Box>
        <Box sx={{ fontSize: 18, fontWeight: 700, mb: 0.75 }}>No lock set</Box>
        <Box
          sx={{
            mx: "auto",
            mb: 2.75,
            maxWidth: 380,
            fontSize: 13.5,
            color: T.muted,
            lineHeight: 1.55,
          }}
        >
          This loan hasn’t been locked yet. Request a lock to record pricing
          terms and capture a snapshot of the loan at lock time.
        </Box>
        <Flx g={1.25} center>
          <Button
            onClick={onSetLock}
            startIcon={<AddRounded sx={{ fontSize: 16 }} />}
            sx={{
              background: T.accent,
              color: "#fff",
              borderRadius: "10px",
              px: 2.25,
              py: 1.4,
              fontSize: 14,
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "0 1px 3px rgba(79,70,229,.4)",
              "&:hover": { background: "#4338ca" },
            }}
          >
            Request a lock
          </Button>
          <Button
            onClick={() => navigate("../sizer")}
            sx={{
              border: `1px solid ${T.line}`,
              background: T.surface,
              color: T.ink2,
              borderRadius: "10px",
              px: 2.25,
              py: 1.4,
              fontSize: 14,
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { background: "#f8fafc" },
            }}
          >
            Open sizer
          </Button>
        </Flx>
      </Box>

      <Box sx={CARD_SX}>
        <Flx jb ac sx={{ mb: 0.25 }}>
          <CardTitle>Readiness checklist</CardTitle>
          <Box sx={{ fontSize: 12.5, color: T.muted, fontWeight: 600 }}>
            {doneCount} of {checklist.length} complete
          </Box>
        </Flx>
        <Box sx={{ fontSize: 12, color: T.faint, mb: 0.5 }}>
          Recommended before locking — none are required to request. Select an
          item to jump to where it's done.
        </Box>
        <Box
          sx={{
            height: 6,
            borderRadius: "99px",
            background: "#eef2f6",
            overflow: "hidden",
            my: 1,
          }}
        >
          <Box
            sx={{
              width: `${(doneCount / checklist.length) * 100}%`,
              height: "100%",
              borderRadius: "99px",
              background: ready ? T.emerald : T.accent,
              transition: "width .3s",
            }}
          />
        </Box>

        <Box>
          {checklist.map((c, i) => (
            <Flx
              key={c.label}
              ac
              g={1.5}
              onClick={c.done ? undefined : () => navigate(c.action.to)}
              sx={{
                p: "13px 8px",
                borderRadius: "9px",
                cursor: c.done ? "default" : "pointer",
                borderBottom:
                  i < checklist.length - 1 ? `1px solid ${T.lineSoft}` : "none",
                "&:hover": { background: c.done ? "transparent" : "#f8fafc" },
              }}
            >
              {c.done ? (
                <CheckCircleRounded sx={{ fontSize: 20, color: T.emerald }} />
              ) : (
                <RadioButtonUnchecked sx={{ fontSize: 20, color: "#cbd5e1" }} />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: c.done ? T.ink : T.ink2,
                  }}
                >
                  {c.label}
                </Box>
                <Box sx={{ fontSize: 12, color: T.faint, mt: "1px", lineHeight: 1.45 }}>
                  {c.done ? c.doneText : c.todoText}
                </Box>
              </Box>
              {!c.done && (
                <Flx
                  ac
                  g={0.25}
                  sx={{
                    flexShrink: 0,
                    color: T.accent,
                    fontSize: 12.5,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {c.action.label}
                  <ChevronRight sx={{ fontSize: 16 }} />
                </Flx>
              )}
            </Flx>
          ))}
        </Box>
      </Box>
    </>
  );
};

export default LoanDrilldownLoanLockTab;
